import React, { useState } from "react";
import { CalendarDays } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ar as arLocale, fr as frLocale, enUS } from "date-fns/locale";
import PhoneInput from "react-phone-number-input";
import { isValidPhoneNumber } from "libphonenumber-js";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useI18n } from "@/lib/i18n";
import { Field, OptionList, StepHeader } from "./Primitives";

const DF_LOCALES = { ar: arLocale, fr: frLocale, en: enUS };
export const RELATIONSHIP_OPTIONS = ["self", "mother", "father", "caregiver", "other"];

export function validateContact(data, t) {
  const errors = {};
  if (!data.beneficiary_name || data.beneficiary_name.trim().length < 2) errors.beneficiary_name = t("validation.nameTooShort");
  if (data.dobMode === "age") {
    if (data.age === "" || data.age === null || data.age === undefined) errors.age = t("validation.dobOrAge");
    else if (Number(data.age) < 0 || Number(data.age) > 120 || !Number.isInteger(Number(data.age))) errors.age = t("validation.ageRange");
  } else {
    if (!data.dob) errors.dob = t("validation.dobOrAge");
    else if (parseISO(data.dob) > new Date()) errors.dob = t("validation.dobFuture");
  }
  if (!data.contact_name || data.contact_name.trim().length < 2) errors.contact_name = t("validation.nameTooShort");
  if (!data.relationship) errors.relationship = t("validation.selectOne");
  if (data.relationship === "other" && !(data.relationship_other || "").trim()) errors.relationship_other = t("validation.specifyOther");
  if (!data.phone || !isValidPhoneNumber(data.phone)) errors.phone = t("validation.phoneInvalid");
  return errors;
}

export function ContactStep({ data, update, errors, stepNumber }) {
  const { t, locale, isRTL } = useI18n();
  const [dobOpen, setDobOpen] = useState(false);
  const dfLocale = DF_LOCALES[locale] || enUS;
  const dobDate = data.dob ? parseISO(data.dob) : undefined;

  return (
    <div className="step-enter">
      <StepHeader number={stepNumber} title={t("step2.title")} subtitle={t("step2.subtitle")} />
      <div className="emerge-card divide-y divide-border px-4 py-1 sm:px-5">
        <Field label={t("step2.beneficiaryName")} htmlFor="beneficiary_name" required error={errors.beneficiary_name}>
          <input
            id="beneficiary_name"
            data-testid="intake-beneficiary-name-input"
            className="emerge-input"
            value={data.beneficiary_name || ""}
            onChange={(e) => update({ beneficiary_name: e.target.value })}
            placeholder={t("step2.beneficiaryNamePlaceholder")}
            autoComplete="off"
            maxLength={120}
            aria-invalid={!!errors.beneficiary_name}
            aria-describedby={errors.beneficiary_name ? "beneficiary_name-error" : undefined}
          />
        </Field>

        <Field label={t("step2.ageOrDob")} htmlFor={data.dobMode === "age" ? "age" : "dob"} required error={errors.dob || errors.age}>
          <Tabs value={data.dobMode || "dob"} onValueChange={(v) => update({ dobMode: v })} className="mb-3">
            <TabsList className="grid h-11 w-full grid-cols-2 rounded-[10px] bg-secondary p-1">
              <TabsTrigger data-testid="intake-dob-tab" value="dob" className="rounded-[8px] text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">
                {t("step2.dobTab")}
              </TabsTrigger>
              <TabsTrigger data-testid="intake-age-tab" value="age" className="rounded-[8px] text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">
                {t("step2.ageTab")}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {data.dobMode === "age" ? (
            <input
              id="age"
              data-testid="intake-age-input"
              type="number"
              inputMode="numeric"
              min={0}
              max={120}
              className="emerge-input"
              value={data.age ?? ""}
              onChange={(e) => update({ age: e.target.value })}
              placeholder={t("step2.agePlaceholder")}
              aria-invalid={!!errors.age}
            />
          ) : (
            <Popover open={dobOpen} onOpenChange={setDobOpen}>
              <PopoverTrigger asChild>
                <button
                  id="dob"
                  type="button"
                  data-testid="intake-dob-calendar-button"
                  className="emerge-input items-center justify-between gap-2"
                  aria-invalid={!!errors.dob}
                >
                  <span className={dobDate ? "text-foreground" : "text-muted-foreground"}>
                    {dobDate ? format(dobDate, "PPP", { locale: dfLocale }) : t("step2.pickDate")}
                  </span>
                  <CalendarDays className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start" dir={isRTL ? "rtl" : "ltr"}>
                <Calendar
                  mode="single"
                  selected={dobDate}
                  onSelect={(d) => {
                    if (d) update({ dob: format(d, "yyyy-MM-dd") });
                    setDobOpen(false);
                  }}
                  captionLayout="dropdown-buttons"
                  fromYear={1920}
                  toYear={new Date().getFullYear()}
                  defaultMonth={dobDate || new Date(new Date().getFullYear() - 6, 0, 1)}
                  disabled={{ after: new Date() }}
                  locale={dfLocale}
                  dir={isRTL ? "rtl" : "ltr"}
                  classNames={{
                    caption_dropdowns: "flex gap-1",
                    dropdown: "rounded-md border border-border bg-white px-1 py-1 text-sm",
                    vhidden: "hidden",
                    caption_label: "hidden",
                  }}
                />
              </PopoverContent>
            </Popover>
          )}
        </Field>

        <Field label={t("step2.contactName")} htmlFor="contact_name" required error={errors.contact_name}>
          <input
            id="contact_name"
            data-testid="intake-contact-name-input"
            className="emerge-input"
            value={data.contact_name || ""}
            onChange={(e) => update({ contact_name: e.target.value })}
            placeholder={t("step2.contactNamePlaceholder")}
            autoComplete="name"
            maxLength={120}
            aria-invalid={!!errors.contact_name}
          />
        </Field>

        <Field label={t("step2.relationship")} required error={errors.relationship || errors.relationship_other}>
          <OptionList
            name={t("step2.relationship")}
            options={RELATIONSHIP_OPTIONS}
            value={data.relationship}
            onChange={(v) => update({ relationship: v })}
            labelFn={(o) => t(`relationships.${o}`)}
            testIdPrefix="intake-relationship"
          />
          {data.relationship === "other" && (
            <input
              data-testid="intake-relationship-other-input"
              className="emerge-input mt-3 step-enter"
              value={data.relationship_other || ""}
              onChange={(e) => update({ relationship_other: e.target.value })}
              placeholder={t("step2.relationshipOther")}
              maxLength={80}
              aria-label={t("step2.relationshipOther")}
              aria-invalid={!!errors.relationship_other}
            />
          )}
        </Field>

        <Field label={t("step2.phone")} htmlFor="phone" required hint={t("step2.phoneHint")} error={errors.phone}>
          <PhoneInput
            id="phone"
            international
            withCountryCallingCode
            countryCallingCodeEditable={false}
            defaultCountry="LB"
            value={data.phone || ""}
            onChange={(v) => update({ phone: v || "" })}
            containerComponentProps={{ "data-testid": "intake-phone-input", "aria-invalid": !!errors.phone }}
            numberInputProps={{ "data-testid": "intake-phone-number-input", autoComplete: "tel", inputMode: "tel", "aria-invalid": !!errors.phone }}
          />
        </Field>
      </div>
    </div>
  );
}
